import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Paragraph from '@tiptap/extension-paragraph';
import Image from '@tiptap/extension-image';
import { commentToHtml } from '../utils/richText';

// Small reusable rich-text editor (TipTap) — bold / italic / bullet & numbered lists / line breaks.
// `value` is the stored value (HTML, or legacy plain text); `onChange` emits HTML ('' when empty).
// `extras` adds underline + text colour (used by the Due Diligence report comments, which are
// client-facing prose rather than an internal note).
const TEXT_COLORS = ['#111111', '#1F72C0', '#B42318', '#15803D', '#B45309', '#6B21A8'];
const INDENT_STEP = 32, INDENT_MAX = 224;
// Indent for ordinary paragraphs. Inside a list, indent/outdent nests the bullet instead (below).
// The margin is emitted as an inline style, which utils/richText.ts's sanitiser explicitly allows.
const IndentParagraph = Paragraph.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      indent: {
        default: 0,
        parseHTML: el => Math.round(parseFloat((el as HTMLElement).style.marginLeft || '0')) || 0,
        renderHTML: attrs => (attrs.indent ? { style: `margin-left: ${attrs.indent}px` } : {}),
      },
    };
  },
});
// `onImage` turns on pictures (Bugify, owner 2026-09-20: 图片支持直接键盘复制黏贴、上传附件，用户
// 操作越简单越好). It uploads one file and resolves to the URL to insert, so this component never
// needs to know where images are stored. Without the prop the editor behaves exactly as before —
// no image extension, no paste handler, no toolbar button.
export default function RichTextEditor({ value, onChange, placeholder, minHeight = 84, extras, onImage }: {
  value: string; onChange: (html: string) => void; placeholder?: string; minHeight?: number; extras?: boolean;
  onImage?: (file: File) => Promise<string>;
}) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  // Held in a ref, not a dep: the editor is created once, and its paste/drop handlers must call
  // whatever the CURRENT prop is rather than the one captured at mount.
  const imgRef = useRef(onImage);
  imgRef.current = onImage;

  const insertFiles = (ed: any, files: File[]) => {
    const up = imgRef.current;
    if (!up || !files.length) return false;
    // Fire and forget per file, in order: the upload is a round-trip and blocking the paste for it
    // would freeze the editor mid-keystroke.
    (async () => {
      for (const f of files) {
        try { const url = await up(f); ed.chain().focus().setImage({ src: url }).run(); }
        catch (e: any) { alert(e?.message || 'image upload failed'); }
      }
    })();
    return true;
  };
  const imageFilesOf = (list: FileList | File[] | null | undefined): File[] =>
    Array.from(list || []).filter(f => /^image\//i.test(f.type));

  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: false }), Placeholder.configure({ placeholder: placeholder || '' }),
      Underline, TextStyle, Color, ...(extras ? [IndentParagraph] : []), ...(onImage ? [Image.configure({ inline: false })] : [])],
    content: commentToHtml(value),
    onUpdate: ({ editor }) => { const html = editor.getHTML(); onChange(html === '<p></p>' ? '' : html); },
    editorProps: {
      handlePaste: (_view, event) => {
        const files = imageFilesOf(event.clipboardData?.files);
        // A screenshot on the clipboard arrives as a file with no name; some apps only expose it
        // through items, so fall back to those before giving up and letting the paste through.
        const items = Array.from(event.clipboardData?.items || [])
          .filter(i => /^image\//i.test(i.type)).map(i => i.getAsFile()).filter(Boolean) as File[];
        const picked = files.length ? files : items;
        if (!picked.length) return false;
        event.preventDefault();
        return insertFiles(editorRef.current, picked);
      },
      handleDrop: (_view, event) => {
        const picked = imageFilesOf((event as DragEvent).dataTransfer?.files);
        if (!picked.length) return false;
        event.preventDefault();
        return insertFiles(editorRef.current, picked);
      },
    },
  });
  // handlePaste/handleDrop are built when the editor is created, so they cannot close over `editor`.
  const editorRef = useRef<any>(null);
  editorRef.current = editor;

  // The Placeholder extension reads its text ONCE, when the editor is built, so a placeholder that
  // depends on state never changed after mount — Bugify's comment box went on saying "Answer the
  // AI…" after the question had been answered. Push the new text into the extension and dispatch an
  // empty transaction, which is what makes ProseMirror recompute its decorations.
  useEffect(() => {
    if (!editor) return;
    const ext = editor.extensionManager.extensions.find(e => e.name === 'placeholder');
    if (!ext || ext.options.placeholder === (placeholder || '')) return;
    ext.options.placeholder = placeholder || '';
    editor.view.dispatch(editor.state.tr);
  }, [placeholder, editor]);

  // Re-seed when the value changes from outside (e.g. switching to another job), but never while the
  // user is typing — that would fight the cursor.
  useEffect(() => {
    if (!editor || editor.isFocused) return;
    const want = commentToHtml(value);
    if ((want || '<p></p>') !== (editor.getHTML() || '<p></p>')) editor.commands.setContent(want, false);
  }, [value, editor]); // eslint-disable-line react-hooks/exhaustive-deps

  // In a list, Tab-style nesting is the natural meaning of indent; elsewhere shift the paragraph.
  const shift = (dir: 1 | -1) => {
    if (!editor) return;
    if (editor.isActive('listItem')) {
      const c = editor.chain().focus();
      (dir > 0 ? c.sinkListItem('listItem') : c.liftListItem('listItem')).run();
      return;
    }
    const cur = Number(editor.getAttributes('paragraph').indent) || 0;
    const next = Math.max(0, Math.min(INDENT_MAX, cur + dir * INDENT_STEP));
    editor.chain().focus().updateAttributes('paragraph', { indent: next }).run();
  };
  const TBtn = ({ on, active, icon, title }: { on: () => void; active?: boolean; icon: string; title: string }) => (
    <button type="button" title={title} onMouseDown={e => { e.preventDefault(); on(); }}
      style={{ border: 'none', background: active ? 'var(--bg-active)' : 'transparent', color: active ? 'var(--accent)' : 'var(--text-secondary)', cursor: 'pointer', borderRadius: 6, padding: '4px 7px', fontSize: 14, lineHeight: 1 }}>
      <i className={`ti ${icon}`} />
    </button>
  );

  return (
    <div style={{ border: '1px solid var(--border-strong)', borderRadius: 9, background: 'var(--c-gray-50)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '4px 6px', borderBottom: '1px solid var(--border)', background: 'var(--bg-surface)' }}>
        <TBtn title="Bold" icon="ti-bold" active={editor?.isActive('bold')} on={() => editor?.chain().focus().toggleBold().run()} />
        <TBtn title="Italic" icon="ti-italic" active={editor?.isActive('italic')} on={() => editor?.chain().focus().toggleItalic().run()} />
        {extras && <TBtn title="Underline" icon="ti-underline" active={editor?.isActive('underline')} on={() => editor?.chain().focus().toggleUnderline().run()} />}
        <div style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 3px' }} />
        {onImage && <TBtn title="Image" icon="ti-photo" on={() => fileRef.current?.click()} />}
        <TBtn title="Bullet list" icon="ti-list" active={editor?.isActive('bulletList')} on={() => editor?.chain().focus().toggleBulletList().run()} />
        <TBtn title="Numbered list" icon="ti-list-numbers" active={editor?.isActive('orderedList')} on={() => editor?.chain().focus().toggleOrderedList().run()} />
        {extras && (<>
          <TBtn title="Outdent" icon="ti-indent-decrease" on={() => shift(-1)} />
          <TBtn title="Indent" icon="ti-indent-increase" on={() => shift(1)} />
          <div style={{ width: 1, height: 16, background: 'var(--border)', margin: '0 3px' }} />
          {TEXT_COLORS.map(c => (
            <button key={c} type="button" title={`Text colour ${c}`} onMouseDown={e => { e.preventDefault(); editor?.chain().focus().setColor(c).run(); }}
              style={{ width: 15, height: 15, borderRadius: '50%', background: c, border: '1px solid var(--border-strong)', cursor: 'pointer', padding: 0, margin: '0 1px' }} />
          ))}
          <TBtn title="Remove colour" icon="ti-ban" on={() => editor?.chain().focus().unsetColor().run()} />
        </>)}
      </div>
      {onImage && <input ref={fileRef} type="file" accept="image/*" multiple hidden
        onChange={e => { if (editor) insertFiles(editor, imageFilesOf(e.target.files)); e.target.value = ''; }} />}
      <EditorContent editor={editor} style={{ minHeight, padding: '8px 11px', fontSize: 13, color: 'var(--text-primary)' }} />
    </div>
  );
}
